import React, { useEffect, useCallback, useState } from "react";
import Head from "next/head";
import useSWR from "swr";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useAuth0 } from "@auth0/auth0-react";
import Cookies from "js-cookie";

import get from "lodash.get";
import isEmpty from "lodash.isempty";

import { HEDGING_BASE_URL } from "@/lib/regex";

import AppLayout from "@/layouts/app-layout";
import FourOFour from "@/components/four-o-four";
import SiteAuthentication from "@/components/site-authentication";
import StackLayout from "@/layouts/stack-layout";
import { HedgingProvider } from "@/context/betaContext";
import { useWindowSize } from "react-use";

function Stencil() {
  const router = useRouter();
  const shouldFetch = router.isReady;
  const { loginWithRedirect, isAuthenticated, logout } = useAuth0();

  const path = shouldFetch
    ? (typeof window !== "undefined" &&
        (window.location.port.includes("3000") ||
          window.location.hostname.includes("netlify"))) ||
      window.location.hostname.includes("proshares-hedging.vshift")
      ? `/${get(router, "query.stencil")?.join("/")}.json`
      : !router?.query?.stencil
      ? `${HEDGING_BASE_URL}/index`
      : `${HEDGING_BASE_URL}/${get(router, "query.stencil")?.join("/")}`
    : null;

  const desktop = get(useWindowSize(), "width") > 1024 ? true : false;

  const { data, error, isLoading } = useSWR(
    shouldFetch ? path : null,
    () => fetch(path).then((res) => res.json()),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    }
  );

  const endpoint =
    (typeof window !== "undefined" &&
      (window.location.port.includes("3000") ||
        window.location.hostname.includes("netlify"))) ||
    window.location.hostname.includes("proshares-hedging.vshift")
      ? `/siteconfig.json`
      : `${HEDGING_BASE_URL}/siteconfig`;

  const {
    data: siteData,
    error: siteError,
    isLoading: siteLoading,
    isValidating,
  } = useSWR(endpoint, () => fetch(endpoint).then((res) => res.json()), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    refreshWhenHidden: false,
    refreshInterval: 0,
    revalidate: false,
  });

  const { sessionTimeOut } = siteData || {};
  const timeOutNumber = sessionTimeOut ? sessionTimeOut : 20;

  // Memoize resetTimer to prevent unnecessary re-renders
  const resetTimer = useCallback(() => {
    localStorage.setItem("lastActivity", Date.now()); // Update last activity time
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    // const resetTimer = () => {
    //   localStorage.setItem("lastActivity", Date.now()); // Update last activity time
    // };

    const checkInactivity = () => {
      const lastActivity = localStorage.getItem("lastActivity");
      const now = Date.now();

      if (lastActivity && now - lastActivity > timeOutNumber * 60 * 1000) {
        // If user has been inactive for 20 minutes, log them out
        router.replace("/session-timeout");
      }
    };

    // Reset activity time on user interaction
    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    window.addEventListener("scroll", resetTimer);

    // Set initial activity time
    resetTimer();

    // Check inactivity every 30 seconds
    const interval = setInterval(checkInactivity, 30 * 1000);

    return () => {
      clearInterval(interval); // Cleanup interval
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      window.removeEventListener("scroll", resetTimer);
    };
  }, [isAuthenticated, resetTimer, router, timeOutNumber]);

  // redirection after login --------------------------------------------------------------
  // useEffect(() => {
  //   const exists = localStorage.getItem("hedgeUrl");
  //   if (
  //     !!router?.query?.portfolio_id &&
  //     !get(router, "query.stencil")?.join("/")?.includes("settings") &&
  //     !isAuthenticated
  //   ) {
  //     if (!exists) {
  //       localStorage.setItem("hedgeUrl", router.asPath);
  //     }

  //     loginWithRedirect();
  //   }
  // }, [isAuthenticated, loginWithRedirect, router]);

  // useEffect(() => {
  //   const hedgeUrl = localStorage.getItem("hedgeUrl");

  //   if (hedgeUrl && isAuthenticated) {
  //     router.push(hedgeUrl);

  //     setTimeout(() => {
  //       localStorage.removeItem("hedgeUrl");
  //     }, 5000);
  //   }
  // }, [isAuthenticated, router]);

  // -----------------------------------------------------------------------------------------

  // redirection after login on manage notifications
  useEffect(() => {
    const exists = localStorage.getItem("manageNotifications");
    if (router?.asPath === "/manage-notifications" && !isAuthenticated) {
      if (!exists) {
        localStorage.setItem("manageNotifications", router.asPath);
      }
      loginWithRedirect();
    }
  }, [isAuthenticated, loginWithRedirect, router]);

  // redirection after login on setting
  useEffect(() => {
    const handleRedirect = () => {
      const settings = localStorage.getItem("settings");
      const stencilPath = get(router, "query.stencil")?.join("/");
      const portfolioId = get(router, "query.portfolio_id");

      if (stencilPath === "hedge-builder/settings" && !isAuthenticated) {
        if (!settings) {
          localStorage.setItem("settings", router.asPath);
          if (portfolioId) {
            localStorage.setItem("portfolio_Id", portfolioId);
          }
        }
        loginWithRedirect();
      }
    };

    // Run only on the client side
    if (typeof window !== "undefined") {
      const timer = setTimeout(handleRedirect, 200);
      return () => clearTimeout(timer);
    }
  }, [router, isAuthenticated, loginWithRedirect]);

  const portfolioId = router.query.portfolio_id;

  // update url path if match with setting and authenticated
  useEffect(() => {
    if (
      get(router, "query.stencil")?.join("/").includes("setting") &&
      isAuthenticated
    ) {
      const newUrl = `/hedge-builder?portfolio_id=${portfolioId}`;
      router.push(newUrl); // Navigates to the new URL
    }
  }, [isAuthenticated, portfolioId, router]);

  useEffect(() => {
    const NotificationUrl = localStorage.getItem("manageNotifications");
    if (NotificationUrl && isAuthenticated) {
      router.push(NotificationUrl);
      localStorage.removeItem("manageNotifications");
    }
  }, [isAuthenticated, router]);

  // store url on localstorage
  useEffect(() => {
    const timeout = setTimeout(() => {
      const host = window.location.host;
      const stencilPath = get(router, "query.stencil")?.join("/");

      if (!stencilPath || isAuthenticated) return;

      let redirectUrl = "";
      if (
        host.includes("hedgewithetfs") &&
        !host.includes("int") &&
        !host.includes("pre-prod")
      ) {
        redirectUrl = "https://www.hedgewithetfs.com/";
      } else if (host.includes("int")) {
        redirectUrl = "https://int.hedgewithetfs.com/";
      } else if (host.includes("pre-prod")) {
        redirectUrl = "https://pre-prod.hedgewithetfs.com/";
      }

      if (redirectUrl) {
        router.push(redirectUrl);
      }
    }, 1500);

    return () => clearTimeout(timeout);
  }, [isAuthenticated, router]);

  // redirection after login
  // if (isLoading && isEmpty(data)) return <></>;

  const [isLoadingProfileData, setIsLoadingProfileData] = useState(true);
  const [initialRenderDone, setInitialRenderDone] = useState(false); // NEW

  useEffect(() => {
    let retries = 0;
    const maxRetries = 10;
    const interval = setInterval(() => {
      const storedData = localStorage.getItem("portfolioData");
      if (storedData) {
        setIsLoadingProfileData(false);
        clearInterval(interval);
      } else if (retries >= maxRetries) {
        setIsLoadingProfileData(false); // stop trying
        clearInterval(interval);
      }
      retries++;
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isAuthenticated !== undefined && !isLoadingProfileData) {
      setInitialRenderDone(true);
    }
  }, [isAuthenticated, isLoadingProfileData]);

  if (!initialRenderDone && window.location.href.includes("hedge-builder")) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center space-y-4">
        <div className="mb-12 h-[200px] w-[200px] animate-spin rounded-full border-b-2 border-t-2 border-primary"></div>
        <div className="text-[25px] font-medium text-primary">
          Please Wait<span className="dots ml-2 inline-block"></span>
        </div>
      </div>
    );
  }

  const {
    className,
    components,
    metaData,
    url,
    contentType,
    noMaster,
    bgDesktop,
    bgMobile,
  } = data || {};

  if (
    (data === undefined && error !== undefined) ||
    !isEmpty(data?.error) ||
    (router.asPath.includes("siteconfig") &&
      !router.asPath.includes("siteconfig.json"))
  ) {
    return <FourOFour />;
  }

  const isCookies = Cookies.get("authenticated") === "true";
  const isLocalhost = window.location.host.includes("localhost");
  const isNetlify = window.location.host.includes("netlify");

  return (
    <>
      {isAuthenticated || window.location.host.includes("localhost") ? (
        <>
          <Head>
            <meta charSet="utf-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            <meta httpEquiv="X-UA-Compatible" content="IE=10" />

            {metaData?.title && <title>{metaData.title}</title>}
            {metaData?.description && (
              <meta name="description" content={metaData.description} />
            )}
            {metaData?.keywords && (
              <meta name="keywords" content={metaData.keywords.toString()} />
            )}

            {/* Open Graph */}
            {metaData?.title && (
              <meta property="og:title" content={metaData.title} />
            )}
            {metaData?.description && (
              <meta property="og:description" content={metaData.description} />
            )}
            {url && <meta property="og:url" content={url} />}
            {metaData?.openGraph?.image && (
              <meta
                property="og:image:url"
                content={metaData.openGraph.image}
              />
            )}
            {metaData?.openGraph?.type && (
              <meta property="og:type" content={metaData.openGraph.type} />
            )}
            {contentType && <meta property="og:type" content={contentType} />}
            {metaData?.title && (
              <meta itemProp="name" content={metaData.title} />
            )}
            {metaData?.description && (
              <meta itemProp="description" content={metaData.description} />
            )}
          </Head>
          {noMaster ? (
            <HedgingProvider>
              <StackLayout components={components} />
            </HedgingProvider>
          ) : (
            <AppLayout>
              <div
                style={{
                  backgroundImage: desktop
                    ? `url(${bgDesktop?.url})`
                    : `url(${bgMobile?.url})`,
                }}
                className={clsx(
                  className,
                  "mt-4 bg-top bg-repeat bg-repeat-x ipad:mt-32"
                )}
              >
                <StackLayout components={components} />
              </div>
            </AppLayout>
          )}
        </>
      ) : null}
    </>
  );
}

export default Stencil;
